import React, { FC, useCallback, useMemo, useState } from 'react';

import { Plus } from '@gravity-ui/icons';
import { Button, Card, Icon, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { Field } from 'client/blocks/form/Field/Field';
import { locateError } from 'client/lib/validate';
import { UserAgreementDraft, UserAgreementOrDraft } from 'client/model/userAgreement';
import { userAgreementsValidators, ValidationErrResult } from 'client/utils/validators';
import * as intentsKeyset from 'i18n/IntentsPage';
import * as keyset from 'i18n/Settings';

import styles from './UserAgreements.module.scss';

const t = i18n(keyset);
const tIntents = i18n(intentsKeyset);

type UAFieldProps = {
    label: string;
    error: string | undefined;
    text: string;
    isEditable: boolean;
    onChange: (_v: string) => void;
};
const UAField = ({ error, label, onChange, text, isEditable }: UAFieldProps) => {
    const [hasChanged, setHasChanged] = useState(false);
    const onChangeCb = useCallback(
        (v: string) => {
            setHasChanged(true);
            onChange(v);
        },
        [onChange],
    );
    return (
        <>
            <div className={styles.userAgreementField__label}>
                {label}
                <span className={styles.boldRed}>*</span>
            </div>
            <TextInput
                className={styles.userAgreementField__input}
                value={text}
                onUpdate={onChangeCb}
                error={hasChanged ? error : undefined}
                errorMessage={hasChanged ? error : undefined}
                size="xl"
                disabled={!isEditable}
            />
        </>
    );
};

type UserAgreementsRowsProps = {
    isEditable: boolean;
    data: UserAgreementOrDraft[];
    onUpdate: (updatedUa: UserAgreementOrDraft[]) => void;
    errors: ValidationErrResult[];
};
const UserAgreementsRows = ({ data, errors, isEditable, onUpdate }: UserAgreementsRowsProps) => {
    const updateItem = (idx: number, field: keyof UserAgreementDraft) => (value: string) =>
        onUpdate([...data.slice(0, idx), { ...data[idx], [field]: value }, ...data.slice(idx + 1)]);

    const errorLocator = useMemo(() => locateError(errors), [errors]);

    const onRemove = React.useCallback(
        (idx: number) => {
            onUpdate(data.filter((_, itemIdx) => itemIdx !== idx));
        },
        [data, onUpdate],
    );

    if (!data.length) {
        return <Card className={clx(styles.card, !isEditable && styles.card_disabled)}>{t('no-user-agreements')}</Card>;
    }

    return (
        <>
            {data.map(({ name, url }, idx) => (
                <Card key={idx} className={styles.card}>
                    <div>
                        <UAField
                            isEditable={isEditable}
                            label={t('ua-name')}
                            text={name}
                            error={errorLocator({ field: 'name', idx })?.error}
                            onChange={updateItem(idx, 'name')}
                        />
                        <UAField
                            isEditable={isEditable}
                            label={t('user-agreements-url')}
                            error={errorLocator({ field: 'url', idx })?.error}
                            text={url}
                            onChange={updateItem(idx, 'url')}
                        />
                    </div>
                    {isEditable && (
                        <Button onClick={() => onRemove(idx)} size="l">
                            {tIntents('button-delete')}
                        </Button>
                    )}
                </Card>
            ))}
        </>
    );
};

interface UserAgreementsProps {
    agreements: UserAgreementOrDraft[];
    onChange: (updatedAgreements: UserAgreementOrDraft[]) => void;
    disabled: boolean;
    isEditable: boolean;
}

export const UserAgreements: FC<UserAgreementsProps> = ({ agreements, onChange, disabled, isEditable }) => {
    const validity = useMemo(() => userAgreementsValidators(agreements), [agreements]);

    const onAdd = React.useCallback(() => {
        onChange([
            ...agreements,
            {
                name: '',
                url: '',
                order: agreements.length,
            },
        ]);
    }, [agreements, onChange]);

    return (
        <Field fieldTitle={t('submenu-agreements')}>
            <UserAgreementsRows
                isEditable={Boolean(isEditable) && !disabled}
                data={agreements}
                onUpdate={onChange}
                errors={validity}
            />
            {isEditable && (
                <Button className={styles.addButton} onClick={onAdd} disabled={disabled} size="l">
                    <Icon data={Plus} />
                    {t('ua-add')}
                </Button>
            )}
        </Field>
    );
};
